import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { useData } from '../../hooks/useData';
import { apiFetch } from '../../api';
import { PageHeader, Button, Modal, ConfirmModal, FormInput, PasswordInput, FormSelect, LoadingSpinner } from '../../components/Common.jsx';
import { DynamicIcon } from '../../components/icons';
import { useNotification } from '../../context/NotificationContext';
import PasswordStrengthIndicator from '../../components/PasswordStrengthIndicator.jsx';
import { validatePassword } from '../../utils/passwordValidation';

// --- Tenant User Form ---
const TenantUserForm = ({ user, roles, onSave, onCancel, tenantId, currentUserEmail, policy }) => {
    const { addNotification } = useNotification();
    const [formData, setFormData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        password: '',
        roleId: user?.roleId || (roles[0]?.id || ''), // safe default
        tenantId: tenantId, // Always scope to the current tenant
    });

    const isEditing = !!user;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const [showWarning, setShowWarning] = useState(false);
    const currentUserDomain = currentUserEmail ? currentUserEmail.split('@')[1] : '';

    // Client-side password validation
    const validation = useMemo(() => validatePassword(formData.password, policy), [formData.password, policy]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // For new users, validate password before showing domain warning
        if (!isEditing && !validation.isValid) {
            addNotification(validation.errorMessage || 'Password does not meet requirements.', 'error');
            return;
        }

        // For edit users, if password entered, validate it
        if (isEditing && formData.password && !validation.isValid) {
            addNotification(validation.errorMessage || 'Password does not meet requirements.', 'error');
            return;
        }

        // Check for external domain if creating a new user or changing email
        const isEmailChanged = isEditing ? formData.email !== user.email : true;
        if (isEmailChanged && formData.email) {
            const newUserDomain = formData.email.split('@')[1];
            if (currentUserDomain && newUserDomain && currentUserDomain !== newUserDomain && !showWarning) {
                setShowWarning(true);
                return;
            }
        }
        
        processSubmit();
    };

    const processSubmit = () => {
        const dataToSave = { ...formData };
        if (isEditing && !dataToSave.password) {
            delete dataToSave.password;
        }
        onSave(dataToSave);
        setShowWarning(false);
    };

    // Strict filter: Tenant Admin can only assign Tenant Admin (r4) or Tenant User (r7)
    const allowedRoleIds = ['r4', 'r7'];
    const filteredRoles = roles.filter(r => allowedRoleIds.includes(r.id));

    return (
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            {/* Dummy hidden inputs to intercept browser autofill */}
            <input type="text" style={{ display: 'none' }} name="fake-email" />
            <input type="password" style={{ display: 'none' }} name="fake-password" />

            <FormInput label="Full Name" id="name" value={formData.name} onChange={handleChange} required autoComplete="off" />
            <FormInput label="Email" id="email" type="email" value={formData.email} onChange={handleChange} required autoComplete="off" />

            <PasswordInput
                label={isEditing ? "Reset Password (leave blank to keep current)" : "Password"}
                id="password"
                value={formData.password}
                onChange={handleChange}
                required={!isEditing}
                placeholder={isEditing ? "••••••••" : ""}
                autoComplete="new-password"
            />
            {/* Show strength indicator for new users always; for edit only when typing */}
            {(!isEditing || formData.password) && (
                <PasswordStrengthIndicator password={formData.password} policy={policy} />
            )}

            <FormSelect label="Role" id="roleId" value={formData.roleId} onChange={handleChange}>
                {filteredRoles.length > 0 ? (
                    filteredRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)
                ) : (
                    <option value="" disabled>No valid roles found</option>
                )}
            </FormSelect>
            <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="secondary" onClick={onCancel} withShadow>Cancel</Button>
                <Button
                    type="submit"
                    disabled={
                        !isEditing
                            ? (!formData.password || !validation.isValid)        // new user: require valid password
                            : (formData.password.length > 0 && !validation.isValid) // edit: optional but must be valid if entered
                    }
                >
                    Save User
                </Button>
            </div>

            <ConfirmModal
                isOpen={showWarning}
                onClose={() => setShowWarning(false)}
                onConfirm={processSubmit}
                title="External Domain Warning"
            >
                The email domain "<strong>{formData.email.split('@')[1]}</strong>" does not match your organization's domain ("<strong>{currentUserDomain}</strong>").
                <br/><br/>
                Are you sure you want to add this user to your organization?
            </ConfirmModal>
        </form>
    );
};

const TenantUsersPage = ({ currentUser, token }) => {
    // Always scope the API call to the current user's tenant — never fetch all users
    const usersUrl = currentUser?.tenantId ? `/users?tenant_id=${currentUser.tenantId}` : '/users';
    const { data: users, isLoading, error, refresh } = useData(usersUrl, token);
    const { data: rolesData, isLoading: rolesLoading } = useData('/roles', token);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [confirmDeactivate, setConfirmDeactivate] = useState(null);
    const [sortOption, setSortOption] = useState('name_asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [passwordPolicy, setPasswordPolicy] = useState(null);
    const [policyCache, setPolicyCache] = useState({});
    const fetchIdRef = React.useRef(0);
    const { addNotification } = useNotification();

    // Enterprise Password Policy Fetching with Caching & Race-Condition Protection
    React.useEffect(() => {
        const targetTenantId = currentUser?.tenantId || 'platform';

        const fetchPolicy = async () => {
            const currentFetchId = ++fetchIdRef.current;

            const cacheKey = `t_${targetTenantId}`;
            const cached = policyCache[cacheKey];

            // TTL Check (5 minutes)
            const now = dayjs().valueOf();
            if (cached && (now - cached.timestamp < 300000)) {
                setPasswordPolicy(cached.policy);
                return;
            }

            try {
                const res = await apiFetch(`/auth/password-policy?tenant_id=${targetTenantId}`, token);

                if (currentFetchId !== fetchIdRef.current) return;

                const newCacheEntry = { policy: res, timestamp: now };
                setPolicyCache(prev => ({ ...prev, [cacheKey]: newCacheEntry }));
                setPasswordPolicy(res);
            } catch (err) {
                console.error("Failed to fetch password policy", err);
            }
        };

        if (token) fetchPolicy();
    }, [currentUser?.tenantId, token]);

    // Filter only tenant-specific users
    const tenantUsers = useMemo(() => {
        if (!users || !currentUser) return [];
        return users.filter(u => u.tenantId === currentUser.tenantId);
    }, [users, currentUser]);

    // Filter tenant-specific roles (fallback = all roles if no scope match)
    const tenantRoles = useMemo(() => {
        if (!rolesData) return [];
        const tenantOnly = rolesData.filter(r => r.scope?.toLowerCase() === 'tenant');
        return tenantOnly.length > 0 ? tenantOnly : rolesData;
    }, [rolesData]);
    const sortedTenantUsers = useMemo(() => {
        const list = [...tenantUsers].filter((u) =>
            (u.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
        list.sort((a, b) => {
            if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
            if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
            if (sortOption === 'status_active') return (a.status === 'Active' ? -1 : 1) - (b.status === 'Active' ? -1 : 1);
            if (sortOption === 'status_inactive') return (a.status === 'Active' ? 1 : -1) - (b.status === 'Active' ? 1 : -1);
            return 0;
        });
        return list;
    }, [tenantUsers, sortOption, searchTerm]);

    const handleAdd = () => {
        setEditingUser(null);
        setIsModalOpen(true);
    };

    const handleSave = async (userData) => {
        const endpoint = editingUser ? `/users/${editingUser.id}` : '/users';
        const method = editingUser ? 'PUT' : 'POST';
        try {
            await apiFetch(endpoint, token, { method, body: JSON.stringify(userData) });
            addNotification(`User "${userData.name}" saved successfully.`);
            refresh();
        } catch (err) {
            addNotification(`Error saving user: ${err.message}`, 'error');
        } finally {
            setIsModalOpen(false);
        }
    };

    const handleDeactivate = (user) => {
        setConfirmDeactivate(user);
    };

    const confirmDeactivation = async () => {
        if (!confirmDeactivate) return;
        try {
            await apiFetch(`/users/${confirmDeactivate.id}`, token, { method: 'DELETE' });
            addNotification(`User "${confirmDeactivate.name}" has been deactivated.`);
            refresh();
        } catch (err) {
            addNotification(`Error deactivating user: ${err.message}`, 'error');
        } finally {
            setConfirmDeactivate(null);
        }
    };

    // Handle loading/error states
    if (isLoading || rolesLoading) return <LoadingSpinner />;
    if (error) return <div className="p-4 text-red-700 bg-red-100 rounded-md">Error: {error.message}</div>;

    return (
        <div>
            <PageHeader title="Organization Users">
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    <input
                        id="userSearch"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search users..."
                        className="h-10 min-w-[220px] rounded-md border border-gray-300 px-3 text-sm bg-white"
                    />
                    <FormSelect id="sortOption" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
                        <option value="name_asc">Name A-Z</option>
                        <option value="name_desc">Name Z-A</option>
                        <option value="status_active">Active First</option>
                        <option value="status_inactive">Inactive First</option>
                    </FormSelect>
                    <Button onClick={handleAdd}>
                        <DynamicIcon name="PlusCircle" className="mr-2 h-4 w-4" /> Add User
                    </Button>
                </div>
            </PageHeader>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">User</th>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Role</th>
                            <th className="p-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedTenantUsers.length > 0 ? (
                            sortedTenantUsers.map(u => (
                                <tr key={u.id}>
                                    <td className="p-4">
                                        <div className="font-medium text-gray-900 dark:text-white">{u.name}</div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">{u.email}</div>
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${u.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                            {u.status}
                                        </span>
                                    </td>
                                    <td className="p-4 text-sm text-gray-500 dark:text-gray-300">{u.role}</td>
                                    <td className="p-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button variant="secondary" className="p-2" onClick={() => {
                                                setEditingUser(u);
                                                setIsModalOpen(true);
                                            }}>
                                                <DynamicIcon name="Edit" className="w-4 h-4" />
                                            </Button>
                                            <Button variant="danger" className="p-2" onClick={() => handleDeactivate(u)}>
                                                <DynamicIcon name="Trash2" className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="p-4 text-center text-gray-500 dark:text-gray-300">
                                    No users found for this tenant.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingUser ? "Edit User" : "Add New User"}
            >
                <TenantUserForm
                    user={editingUser}
                    roles={tenantRoles}
                    onSave={handleSave}
                    onCancel={() => setIsModalOpen(false)}
                    tenantId={currentUser.tenantId}
                    currentUserEmail={currentUser.email}
                    policy={passwordPolicy}
                />
            </Modal>

            <ConfirmModal
                isOpen={!!confirmDeactivate}
                onClose={() => setConfirmDeactivate(null)}
                onConfirm={confirmDeactivation}
                title={`Deactivate ${confirmDeactivate?.name}?`}
            >
                Are you sure you want to deactivate this user? They will no longer be able to access the portal.
            </ConfirmModal>
        </div>
    );
};

export default TenantUsersPage;
